<script setup>
defineOptions({ name: 'LlmModesPage' })
/**
 * 场景路由页 —— 把某个场景的主力从 A 模型换成 B，点保存，不重启服务，下一次调用就用 B
 * （后端写事务提交后 bump 配置版本 + `llm.invalidate()`）。
 *
 * 布局：顶部批量替换（`mode/BulkReplace.vue`），下面一张总表一行一个场景、整条链一眼看完；
 * 点行在右侧抽屉里编辑单个场景（`mode/ModeEditor.vue`，链怎么编辑在 `mode/ChainEditor.vue`）。
 * 批量替换选中的模型会在总表里高亮，先看清会影响哪些场景再动手。
 *
 * 可选的 (厂商, 模型) 要一个个厂商详情去取 —— 列表接口只给数量，不给模型名。配置表就几十行，
 * 这几个请求并发发一次，比给后端加一个只有这一页用的聚合接口划算。
 */
import { computed, onMounted, ref } from 'vue'
import { useRouter } from 'vue-router'
import { ArrowRight } from '@lucide/vue'
import { Badge, Button, Drawer, EmptyState, PageHeader, Section, Spinner, Table } from '@drama/ui'
import { useI18n } from 'vue-i18n'
import { fetchModelCapabilityCatalog } from '../../api/model-capabilities.js'
import { listLlmModes } from '../../api/llm/index.js'
import { chainOptions, stepKey } from './mode-chain.js'
import BulkReplace from './mode/BulkReplace.vue'
import ModeEditor from './mode/ModeEditor.vue'

const { t, te } = useI18n()
const router = useRouter()

/** @type {import('vue').Ref<import('../../api/llm/index.js').ModeRow[]>} */
const modes = ref([])
/** @type {import('vue').Ref<import('./mode-chain.js').ChainStep[]>} */
const options = ref([])
const loading = ref(false)
const error = ref('')
/** 批量替换里选中的「要换掉的」模型，总表据此高亮。 */
const highlight = ref('')
/** 抽屉里正在编辑的场景名；空 = 抽屉关闭。 */
const editing = ref('')

const visibleModes = computed(() => modes.value.filter(mode => mode.mode !== 'novel_structured'))
const noOptions = computed(() => options.value.length === 0)
const routable = computed(() => new Set(options.value.map(stepKey)))
const editingMode = computed(() => visibleModes.value.find(mode => mode.mode === editing.value) ?? null)

const columns = computed(() => [
  { key: 'scene', label: t('visualGameAdmin.llm.modes.table.scene'), width: '300px', wrap: true },
  { key: 'chain', label: t('visualGameAdmin.llm.modes.table.chain'), width: '640px', wrap: true },
  { key: 'actions', label: '', width: '96px', align: 'right' },
])

onMounted(load)

async function load() {
  loading.value = true
  error.value = ''
  try {
    const [modeList, catalog] = await Promise.all([listLlmModes(), fetchModelCapabilityCatalog()])
    modes.value = modeList.items
    options.value = chainOptions(catalog)
  } catch (err) {
    error.value = err.message
  } finally {
    loading.value = false
  }
}

function sceneName(mode) {
  const key = `visualGameAdmin.storyModes.${mode}.name`
  return te(key) ? t(key) : mode
}

/** 保存成功后后端返回的就是新的那一行，直接换进列表 —— 不再回查一次。 */
function onModeSaved(saved) {
  modes.value = modes.value.map((mode) => (mode.mode === saved.mode ? { ...saved, required: mode.required } : mode))
}

function onEditorSaved(saved) {
  onModeSaved(saved)
  editing.value = ''
}
</script>

<template>
  <div class="vga-page">
    <PageHeader :title="t('visualGameAdmin.llm.modes.title')" />

    <Section v-if="loading">
      <div class="modes__state">
        <Spinner :label="t('visualGameAdmin.common.loading')" />
        <span>{{ t('visualGameAdmin.common.loading') }}</span>
      </div>
    </Section>

    <Section v-else-if="error">
      <div
        class="modes__state"
        role="alert"
      >
        <p class="vga-error">
          {{ error }}
        </p>
        <Button
          variant="secondary"
          @click="load"
        >
          {{ t('visualGameAdmin.common.retry') }}
        </Button>
      </div>
    </Section>

    <Section
      v-else-if="noOptions"
      data-insp-path="apps/visual-game-admin/src/pages/llm/Modes.vue#no-options"
    >
      <EmptyState
        :title="t('visualGameAdmin.llm.modes.noOptions.title')"
        :description="t('visualGameAdmin.llm.modes.noOptions.description')"
      >
        <template #action>
          <Button
            variant="primary"
            @click="router.push({ name: 'llm-providers' })"
          >
            {{ t('visualGameAdmin.llm.modes.noOptions.action') }}
          </Button>
        </template>
      </EmptyState>
    </Section>

    <Section
      v-else-if="visibleModes.length === 0"
      data-insp-path="apps/visual-game-admin/src/pages/llm/Modes.vue#empty"
    >
      <EmptyState
        :title="t('visualGameAdmin.llm.modes.empty.title')"
        :description="t('visualGameAdmin.llm.modes.empty.description')"
      >
        <template #action>
          <Button
            variant="primary"
            @click="router.push('/llm/capabilities')"
          >
            {{ t('visualGameAdmin.llm.modes.empty.action') }}
          </Button>
        </template>
      </EmptyState>
    </Section>

    <template v-else>
      <Section data-insp-path="apps/visual-game-admin/src/pages/llm/Modes.vue#table">
        <BulkReplace
          v-model:from="highlight"
          class="modes__toolbar"
          :modes="visibleModes"
          :options="options"
          @saved="onModeSaved"
        />
        <Table
          :columns="columns"
          :rows="visibleModes"
          row-key="mode"
          clickable
          @row-click="row => (editing = row.mode)"
        >
          <template #scene="{ row }">
            <div class="modes__scene">
              <strong>{{ sceneName(row.mode) }}</strong>
              <span
                v-if="sceneName(row.mode) !== row.mode || row.required || !row.enabled"
                class="modes__flags"
              >
                <code v-if="sceneName(row.mode) !== row.mode">{{ row.mode }}</code>
                <Badge
                  v-if="row.required"
                  variant="warning"
                >
                  {{ t('visualGameAdmin.llm.modes.required') }}
                </Badge>
                <Badge
                  v-if="!row.enabled"
                  variant="danger"
                >
                  {{ t('visualGameAdmin.llm.modes.status.disabled') }}
                </Badge>
              </span>
            </div>
          </template>
          <template #chain="{ row }">
            <!-- 主力是实心标签，兜底是跟在后面的浅色文字：一眼先看到每个场景实际在用谁。 -->
            <ol class="modes__chain">
              <li
                v-for="(step, index) in row.chain"
                :key="index"
                class="modes__step"
                :class="{
                  'is-primary': index === 0,
                  'is-highlight': highlight && stepKey(step) === highlight,
                  'is-unroutable': !routable.has(stepKey(step)),
                }"
                :title="routable.has(stepKey(step)) ? `${step.provider} / ${step.model}` : t('visualGameAdmin.llm.modes.errors.unroutable')"
              >
                <ArrowRight
                  v-if="index > 0"
                  class="modes__arrow"
                  :size="12"
                  aria-hidden="true"
                />
                <span class="modes__chip"><span class="modes__provider">{{ step.provider }} /</span> {{ step.model }}</span>
              </li>
            </ol>
          </template>
          <template #actions="{ row }">
            <Button
              class="modes__edit"
              variant="ghost"
              size="sm"
              @click.stop="editing = row.mode"
            >
              {{ t('visualGameAdmin.llm.modes.table.edit') }}
            </Button>
          </template>
        </Table>
      </Section>
    </template>

    <Drawer
      :open="editingMode !== null"
      :title="editingMode ? sceneName(editingMode.mode) : ''"
      width="640px"
      @update:open="open => { if (!open) editing = '' }"
    >
      <ModeEditor
        v-if="editingMode"
        :key="editingMode.mode"
        :mode="editingMode"
        :options="options"
        @saved="onEditorSaved"
      />
    </Drawer>
  </div>
</template>

<style scoped>
.modes__toolbar {
  margin-bottom: var(--drama-space-lg);
  padding-bottom: var(--drama-space-lg);
  border-bottom: 1px solid var(--drama-color-border);
}

.modes__scene {
  display: grid;
  gap: 2px;
}

.modes__flags {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: var(--drama-space-xs);
  color: var(--drama-color-text-muted);
  font-size: var(--drama-font-size-xs);
}

.modes__chain {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: var(--drama-space-xs) var(--drama-space-sm);
  margin: 0;
  padding: 0;
  list-style: none;
  font-family: var(--drama-font-family-mono);
  font-size: var(--drama-font-size-xs);
}

.modes__step {
  display: flex;
  align-items: center;
  gap: var(--drama-space-sm);
  color: var(--drama-color-text-muted);
}

.modes__arrow {
  opacity: .6;
}

.modes__chip {
  padding: 2px 0;
  border-radius: var(--drama-radius-sm);
  white-space: nowrap;
}

.modes__provider {
  opacity: .7;
}

.modes__step.is-primary .modes__chip {
  padding: 3px var(--drama-space-sm);
  background: var(--drama-color-surface-alt);
  color: var(--drama-color-text);
  font-weight: var(--drama-font-weight-medium);
}

.modes__step.is-highlight .modes__chip {
  padding: 3px var(--drama-space-sm);
  background: color-mix(in srgb, var(--drama-color-warning) 22%, transparent);
  color: var(--drama-color-text);
}

.modes__step.is-unroutable .modes__chip {
  color: var(--drama-color-danger);
  text-decoration: line-through;
}

.modes__edit {
  color: var(--drama-color-text-muted);
}

.modes__state {
  display: grid;
  justify-items: start;
  gap: var(--drama-space-md);
  color: var(--drama-color-text-muted);
}
</style>
